<template>
  <!-- `role="dialog"` only in the floating chrome. Over the page the panel is a
       window with focus moved into it, which is what that role describes; in the
       band it is content in the flow of a page, and calling a card in a paragraph
       a dialog would be wrong. The name is the one the header already prints, so
       the two cannot disagree. -->
  <div
    class="ai-chat"
    :class="'is-' + variant"
    :role="isPanel ? 'dialog' : null"
    :aria-label="isPanel ? name : null"
  >
    <div class="ai-chat-head">
      <span class="ai-chat-avatar" aria-hidden="true">
        <img src="/images/chat/robot.svg" alt="" />
      </span>
      <div class="ai-chat-who">
        <p class="ai-chat-name">{{ name }}</p>
        <p class="ai-chat-status">
          <status-dot />
          {{ t('Always online 24/7') }}
        </p>
      </div>
    </div>

    <!-- `role="log"` says what this is: a chat transcript. It also carries an
         implicit `aria-live="polite"`, and that is the problem it used to have.
         Token streaming mutates this node many times a second, so the whole
         transcript was announced fragment by fragment and the chat was
         unusable with a screen reader. The explicit `off` overrides the
         implicit value, which keeps the transcript semantics and moves the
         announcing to the dedicated region below. -->
    <div class="ai-chat-log-wrap">
    <div
      ref="log"
      class="ai-chat-log"
      role="log"
      aria-live="off"
      :aria-label="name"
      tabindex="0"
      :class="{ 'is-restoring': isHydrating }"
      @scroll="onScroll"
      @wheel="releaseFollow"
      @touchmove="releaseFollow"
      @pointerdown="releaseFollow"
    >
      <!-- The empty state, and not a message. Nothing here is written to the
           transcript, counted or sent to the model, so a restored thread renders
           exactly like a fresh one and the opening words are not paid for on
           every turn. -->
      <template v-if="isEmpty">
        <div class="ai-chat-bubble is-bot">
          <div class="prose-inline" v-html="bodyHtml(t('👋 Hi there! Welcome to {site}. I am here to help you find your way.', { site: name }))"></div>
        </div>
        <div class="ai-chat-bubble is-bot">
          <div class="prose-inline" v-html="bodyHtml(t('What would you like to know about?'))"></div>
        </div>
      </template>

      <!-- Waiting to be told whether this thread is empty.

           A rested thread's turns arrive a frame or two after the surface does,
           and until they do the panel cannot tell "nothing has been said" from
           "not answered yet". Showing the greeting for that moment is what made
           the opening flash.

           This is not the typing bubble. Those three dots mean the assistant is
           writing a reply, and a reply-shaped bubble in an empty log reads as
           that. Restoring a transcript is a different wait, so it sits in the
           middle of the log, as its own mark, until the thread arrives. -->
      <div v-else-if="isHydrating" class="ai-chat-history" role="status">
        <span class="ai-chat-history-mark" aria-hidden="true">
          <span class="ai-chat-dots">
            <span></span><span></span><span></span>
          </span>
        </span>
        <p class="ai-chat-history-label">{{ t('Loading the conversation') }}</p>
      </div>

      <!-- A finished turn's narration, above the answer it produced, collapsed
           by default: the answer is what the visitor came for and the trace is
           the detail behind it. -->
      <template v-for="(message, index) in messages" :key="message.id || index">
        <div v-if="message.reasoning" class="ai-chat-think">
          <button
            class="ai-chat-think-head"
            type="button"
            :aria-expanded="isThinkOpen(index) ? 'true' : 'false'"
            @click="toggleThink(index, $event)"
          >
            <span class="disclosure-mark" aria-hidden="true"></span>
            <span class="ai-chat-think-label">{{ thinkLabel(message.reasoning.ms, false) }}</span>
          </button>
          <div class="ai-chat-think-body" :class="{ 'is-open': isThinkOpen(index) }">
            <div class="ai-chat-think-text">
              <div class="prose-inline" v-html="bodyHtml(message.reasoning.text)"></div>
            </div>
          </div>
        </div>
        <div v-if="message.cards && message.cards.length" class="ai-chat-cards">
          <figure v-for="(card, cardIndex) in message.cards" :key="cardIndex" class="ai-chat-card">
            <img v-if="card.image" :src="card.image" alt="" @load="onCardImage">
            <div v-if="card.caption" class="prose-inline" v-html="bodyHtml(card.caption)"></div>
          </figure>
        </div>
        <div v-if="message.text" class="ai-chat-bubble" :class="['is-' + message.role, { 'is-settled': message.settled }]">
          <div class="prose-inline" v-html="bodyHtml(message.text)"></div>
        </div>
      </template>

      <!-- The turn in flight's narration. The reply is held until the narration
           has been drawn, and the trace folds once that drawing is done. -->
      <div v-if="reasoningText" class="ai-chat-think">
        <button
          class="ai-chat-think-head"
          type="button"
          :aria-expanded="thinkOpen ? 'true' : 'false'"
          @click="setThinkOpen(!thinkOpen, $event)"
        >
          <span class="disclosure-mark" aria-hidden="true"></span>
          <span class="ai-chat-think-label">{{ thinkLabel(reasoningMs, reasoningActive) }}</span>
        </button>
        <div class="ai-chat-think-body" :class="{ 'is-open': thinkOpen }">
          <div class="ai-chat-think-text">
            <div class="prose-inline" v-html="reasoningHtml"></div>
          </div>
        </div>
      </div>

      <!-- Cards for the turn in flight, ahead of the message they belong to. -->
      <div v-if="liveCards.length" class="ai-chat-cards">
        <figure v-for="(card, cardIndex) in liveCards" :key="cardIndex" class="ai-chat-card">
          <img v-if="card.image" :src="card.image" alt="" @load="onCardImage">
          <div v-if="card.caption" class="prose-inline" v-html="bodyHtml(card.caption)"></div>
        </figure>
      </div>

      <!-- The live bubble. Hidden while the narration is still open and the
           reply has not started, so the dots do not sit under a trace that is
           still being written. Also hidden while a pack is still arriving, so
           the message follows the cards. Only this one re-renders per frame
           while an answer streams, and `aria-busy` is what tells a screen
           reader it is still being written rather than a finished thought. -->
      <div v-if="isTyping && showLiveAnswer" class="ai-chat-bubble is-bot is-typing">
        <div v-if="streamHtml" class="prose-inline" aria-busy="true" v-html="streamHtml"></div>
        <span v-else class="ai-chat-dots" aria-hidden="true">
          <span></span><span></span><span></span>
        </span>
        <span class="ai-chat-sr">{{ t('Typing') }}</span>
      </div>
    </div>

    <!-- The way back to the newest text.

         Offered whenever the log has stopped following, which is to say
         whenever there is text below the bottom of the window. It floats over
         the bottom of the transcript rather than taking a row of its own: a
         row would push the chips and the field each time the visitor scrolled,
         which is the fold the chips already do and the wrong motion for a
         control that is only a way back. Closed, it takes no space and no
         clicks, so the log underneath stays what the pointer is on. -->
    <app-button
      class="ai-chat-jump"
      :class="{ 'is-open': showJump }"
      variant="outline"
      icon="arrow-down"
      @click="jumpToLatest(true)"
    >
      {{ t('Jump to latest') }}
    </app-button>
    </div>

    <!-- The one live region. It receives the finished text of the newest turn,
         once, and never a fragment of one.

         Rendered by whichever surface is the only one on screen. Where two
         surfaces draw this thread, the second would announce the same sentence
         again, so it is turned off there and the words are said once. -->
    <p v-if="announce" class="ai-chat-sr" aria-live="polite">{{ announcement }}</p>

    <!-- The chips, and their fold.

         They used to stay put while a new set was being generated, so the row
         never flashed empty. That is wrong once a chip is also how a visit
         starts: the row that was just used goes on offering the questions the
         visitor has already asked, and it sits between them and the answer they
         are waiting for. So it folds away on the send and comes back when the
         next set arrives, which is when there is something new to offer.

         A follow-up chip is a question the visitor might ask next. A choice chip
         is an answer to the question in the newest reply, and a tap sends those
         words. Typing still works either way. -->
    <div class="ai-chat-chips" :class="{ 'is-open': !chipsFolded, 'is-choices': chipMode === 'choices' }">
      <div class="ai-chat-chips-row">
        <button
          v-for="(chip, index) in suggestions"
          :key="chip"
          class="ai-chat-chip"
          type="button"
          :style="{ '--chip-index': index }"
          :title="chip"
          @click="ask(chip)"
        >
          <span v-if="chipMode === 'choices'" class="ai-chat-chip-mark">{{ choiceLetter(index) }}</span>
          <span class="ai-chat-chip-label">{{ chip }}</span>
        </button>
      </div>
    </div>

    <form class="ai-chat-form" @submit.prevent="submit()">
      <div class="ai-chat-checks">
        <div class="ai-chat-check">
          <div v-show="checking && !challengeOpen" class="ai-chat-check-wait" role="status">
            <font-awesome-icon icon="spinner" spin />
            <span>{{ t('Verifying you are human...') }}</span>
          </div>
          <div ref="messageCheck"></div>
        </div>
      </div>
      <div class="ai-chat-compose">
        <label class="ai-chat-sr" :for="inputId">{{ t('Your question') }}</label>
        <input
          :id="inputId"
          ref="field"
          v-model="draftText"
          class="ai-chat-input"
          type="text"
          autocomplete="off"
          :maxlength="maxLength"
          :disabled="disabled || checking"
          :placeholder="chipMode === 'choices' ? t('Or type your own answer') : t('Type your question...')"
        />
        <app-button
          class="ai-chat-send"
          type="submit"
          icon="paper-plane"
          :aria-label="t('Send')"
          :loading="checking"
          :disabled="disabled || !draftText.trim() || isTyping || checking"
        />
      </div>
    </form>

    <!-- The state line, if there is one. A state of the surface rather than
         something the assistant said, so it sits under the form instead of in
         the log: the assistant being unreachable at all, or a single attempt
         that failed or hit the limit. -->
    <p v-if="stateLine" class="ai-chat-notice">{{ stateLine }}</p>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The assistant's surface, and nothing else.

   This file draws the panel; it does not own the conversation. Messages,
   suggestions, the stream and the unavailable state all arrive as props, and
   the only thing it emits is `send`. Both call sites read the same composable
   (useChat), so a visitor who asks something in the floating panel and then
   opens the AI page sees one conversation rather than two.

   Deliberately not Chat.vue: that name, and the `.chat` class, belong to the
   floating launcher, which renders this panel inside its own chrome. This one
   is the surface, so it owns its own root class.

   Bubbles, chips and the input are parts of this one surface rather than new
   sitewide components. The two things it does reuse are the ones that already
   have an owner: the button and every colour, radius, shadow and duration from
   _tokens.scss. The typography of a rendered reply is not declared here either:
   `.prose-inline` in _prose.scss owns it, because that is where the site keeps
   the typography of generated HTML. */

.ai-chat {
  background: $ground;
  border: 1px solid $line-blue;
  /* $r-md, not the $r-xl this card used to carry.

     The site's large surfaces take $r-xl (InkSlab, Strength) and its content
     cards take $r-lg (PostCard, PanelList, PlanTable), and this panel is a large
     surface, so $r-xl was not drift. It is the one exception, and it is
     deliberate: this is a window rather than a band on the page, and at 1.75rem
     the corners read as a soft blob next to the 0.7rem bubbles, chips and
     avatar sitting inside it. A panel whose own corners are rounder than
     everything in it looks inflated rather than soft.

     $r-md makes the card's corner the same measure as the bubbles it holds,
     which is the relationship that reads as one surface. It is a one-off and
     stays one: if a third floating window appears, this value moves into a
     shared owner rather than being copied. */
  border-radius: $r-md;
  box-shadow: $shadow-band;
  /* Wide enough that the box reads as the band's focus without the panel going
     half empty at the message lengths a chat actually produces. One
     declaration, so it is one number to change.

     The bottom margin is larger than the gap the band actually wants, because
     $shadow-band travels 4.25rem below the card: at a normal gap the card's own
     shadow landed on the ruled list underneath it and the two surfaces read as
     one smudged edge. This clears it, and it is what spaces the list below
     (the page declares no margin-top there). */
  margin: 0 auto 4rem;
  max-width: 46rem;
  overflow: hidden;
}

/* The floating chrome. A window over the page rather than a band in it: it
   fills the width the launcher gives it, sits flush against the launcher
   instead of holding the band's shadow clearance, and takes a hover card's
   shadow rather than the band's. Nothing else changes, which is the point of
   one panel in two chromes. */
.ai-chat.is-panel {
  box-shadow: $shadow-lift;
  margin: 0;
  max-width: none;

  /* The transcript is what gives way in a short viewport. The head, the chips
     and the form are the panel's fixed furniture, and the launcher sits below
     all three, so a log sized only in rem would push the top of the panel off
     the screen on a phone. `min` with `dvh` is the cap; the rem lines in the
     base rule are the fallback for a browser without `dvh`, where the whole
     declaration is dropped rather than half-applied.

     The floor takes the same `min()` as the cap, and that is not symmetry for
     its own sake: a floor above the cap wins, and on a short viewport it would
     quietly reinstate the exact overflow the cap exists to prevent. Bounding
     both by the same 58dvh means the two can meet but cannot cross. */
  .ai-chat-log {
    min-height: min(22rem, 58dvh);
    max-height: min(34rem, 58dvh);
  }

  /* The answer list sits under the log. Pulling the transcript in keeps the
     options and the field on screen instead of growing the window off the top. */
  &:has(.ai-chat-chips.is-choices) .ai-chat-log {
    max-height: min(24rem, 42dvh);
    min-height: min(16rem, 42dvh);
  }
}

/* The phone sheet. The panel has taken the viewport; this surface fills that
   box rather than sitting in it as the corner card. The log is the one part
   that grows, so the head, the chips and the field stay put and the transcript
   scrolls between them. Square corners and no card shadow, because a rounded
   card floating inside a full screen shows the page in the gaps. */
@media (max-width: 767.98px) {
  .ai-chat.is-panel {
    border: 0;
    border-radius: 0;
    box-shadow: none;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    height: auto;
    min-height: 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    width: 100%;

    .ai-chat-head {
      padding-right: 4.5rem;
      padding-top: calc(0.85rem + env(safe-area-inset-top, 0px));
    }

    .ai-chat-log-wrap {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }

    .ai-chat-log {
      flex: 1 1 auto;
      max-height: none;
      min-height: 0;
      overscroll-behavior: contain;
    }
  }
}

.ai-chat-head {
  align-items: center;
  background: $ground-tint;
  border-bottom: 1px solid $line;
  display: flex;
  gap: 0.85rem;
  padding: 1rem 1.25rem;
}

.ai-chat-avatar {
  align-items: center;
  /* Primary, not the soft tint. The mark is a white robot with a blue face
     plate, so it needs a coloured ground to read at all: on the pale tint the
     white body disappeared into the card and only the face plate showed. On
     $primary the silhouette is what the eye lands on. */
  background: $primary;
  border-radius: $r-md;
  display: inline-flex;
  flex: 0 0 auto;
  /* The tile keeps its original 2.4rem. The mark inside it was the part that
     read small, and it went up twice: 1.4rem left 0.5rem of padding a side,
     1.8rem left 0.3rem. 2.1rem leaves a 0.15rem hairline of blue, which is the
     thinnest inset that still reads as a tile rather than a clipped mark. */
  height: 2.4rem;
  justify-content: center;
  width: 2.4rem;

  img {
    height: 2.1rem;
    width: 2.1rem;
  }
}

/* The name and the status are one column beside the tile, never two lines
   running together. `.ai-chat-who` had no rule of its own, so the stack relied
   on the two paragraphs being block elements and their 1.3 line-heights, which
   at these sizes read as a single line of text. Declaring the column makes the
   two ranks explicit, and `justify-content: center` is what centres the column
   against the tile rather than letting it sit at the top of the head row. */
.ai-chat-who {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  justify-content: center;
  min-width: 0;
}

.ai-chat-name {
  color: $text-heading;
  font-family: $headings-font-family;
  font-size: 0.98rem;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-chat-status {
  align-items: center;
  color: $text-label;
  display: flex;
  font-size: 0.78rem;
  gap: 0.4rem;
  line-height: 1.3;
  margin: 0;
}

/* The log scrolls, so the keyboard has to be able to reach it. Its ring is
   inset, because the card's own overflow would clip an outset one.

   It is floored, not just capped. Without the floor the box is only as tall as
   the two greeting bubbles, so a fresh visitor meets a short panel that then
   jumps taller on the first answer, and on the band that jump moves the whole
   section below it. The floor gives the empty state the size of the window it
   is about to fill, and the answering turn grows into it instead of resizing
   the card. It sits under the 34rem cap so there is still room to grow
   into, and it is the one number to change if the empty box wants to be taller.

   Both bounds are a plain rem here, and the panel below overrides each with a
   `min()` against `dvh`. That order is the fallback: a browser without `dvh`
   drops the invalid declaration and keeps these, rather than half-applying a
   cap it cannot honour. */
.ai-chat-log {
  background: $ground;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 22rem;
  max-height: 34rem;
  /* The browser's own scroll anchoring is turned off, because this log is pinned
     to its bottom explicitly as the answer is revealed. Both want to move the
     same scroll position, and when they disagree the visible result is a fight:
     anchoring re-adjusts for a node the pin has just measured, and the log
     twitches a few pixels on the frames where the two do not agree.

     Nothing is lost by turning it off, because the pin is the better behaviour
     here: it follows the bottom rather than holding one node still. */
  overflow-anchor: none;
  overflow-y: auto;
  padding: 1.35rem 1.25rem;

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: -3px;
  }

  /* Restoring a thread. The log's only child is the history mark, and centering
     it here is what puts that mark in the middle of the window rather than at
     the top, where a bubble would sit. */
  &.is-restoring {
    align-items: center;
    justify-content: center;
  }
}

/* The reasoning trace, above the answer it produced.

   Not a bubble: it is not part of the reply, it is never persisted, and it is a
   disclosure rather than something the assistant said, so it sits bare on the
   log's own ground. The mark is the shared `.disclosure-mark` from
   _utilities.scss, which draws the plus and the minus and reads the open state
   off the `aria-expanded` on the button above it, so this file owns neither.

   The typography of the rendered trace is not declared here either. The size is
   on this wrapper and `.prose-inline` inherits it, which is the same split the
   bubbles use. */
.ai-chat-think {
  align-self: flex-start;
  /* Pulled up against the bubble it belongs to. The log's gap spaces one turn
     from the next, and a trace is part of its own turn rather than a turn. */
  margin-bottom: -0.35rem;
  max-width: 100%;
  min-width: 0;
}

.ai-chat-think-head {
  align-items: center;
  background: none;
  border: 0;
  color: $text-label;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  gap: 0.45rem;
  line-height: 1.4;
  padding: 0.15rem 0;
  text-align: left;
  transition: color $dur-quick $ease;

  &:hover {
    color: $primary;
  }

  &:focus-visible {
    border-radius: $r-xs;
    outline: 3px solid $ring-focus;
    outline-offset: 2px;
  }

  /* Size only. The plus and the minus are still the shared mark. This label is
     the small muted line, so the mark steps down from the size the FAQ uses. */
  .disclosure-mark {
    height: 0.6rem;
    width: 0.6rem;
  }
}

/* The disclosure's body, and its fold.

   The site's collapse, the same one the chip row above uses, so the trace opens
   and closes with the motion the rest of the site moves with instead of the
   hard cut a `v-show` gave it. That cut mattered most on the automatic fold: the
   narration collapses once it has been written out, and the reply starts then.
   Snapping it shut on the first answer token, while the trace was still being
   drawn, read as the trace being taken away rather than handing over.

   The inset is this file's. It sits outside the grid track, so it is zero while
   folded and returns with the open state; leaving it would hold a closed trace
   open by 0.4rem and separate it from the head it belongs to. */
.ai-chat-think-body {
  @include collapse($also: margin $dur $ease);

  margin: 0;

  &.is-open {
    margin-top: 0.4rem;
  }
}

/* The trace's own type, on the child rather than on the track.

   It has to be inside the grid track so that it collapses with the block; on the
   track it would keep its padding and its rule on screen while the track was at
   zero height. */
.ai-chat-think-text {
  border-left: 2px solid $line;
  color: $text-muted;
  font-size: 0.86rem;
  line-height: 1.6;
  padding-left: 0.75rem;
}

.ai-chat-bubble {
  animation: ai-chat-rise $dur $ease both;
  border-radius: $r-md;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  max-width: 86%;
  padding: 0.7rem 0.95rem;

  &.is-bot {
    align-self: flex-start;
    background: $ground-tint;
    border: 1px solid $line;
    /* The corner nearest the speaker is tightened, so the side a bubble comes
       from is legible without a tail. */
    border-bottom-left-radius: $r-xs;
    color: $text-strong;
  }

  &.is-user {
    align-self: flex-end;
    background: $primary;
    border-bottom-right-radius: $r-xs;
    color: $on-dark-strong;
  }

  /* The turn the reveal has just finished. The visitor watched this bubble being
     written a frame at a time, so replaying the entrance at the moment it takes
     its place in the transcript is the same answer arriving twice. The flag is
     set on the message by the composable and lives only in memory, so a restored
     thread still animates in like the rest of the transcript. */
  &.is-settled {
    animation: none;
  }
}

.ai-chat-cards {
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  max-width: 86%;
}

/* The same box as a bot bubble. The picture sits in the padding, and the
   caption uses the bubble's type, so a card reads as another message. */
.ai-chat-card {
  animation: ai-chat-rise $dur $ease both;
  background: $ground-tint;
  border: 1px solid $line;
  border-radius: $r-md;
  border-bottom-left-radius: $r-xs;
  color: $text-strong;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  padding: 0.7rem 0.95rem;

  img {
    border-radius: $r-xs;
    display: block;
    height: auto;
    margin: 0 0 0.6rem;
    width: 100%;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* The typing dots. The only new keyframe on the page, and the only place the
   stagger lives. */
.ai-chat-dots {
  display: inline-flex;
  gap: 0.28rem;

  span {
    animation: ai-chat-blink $dur-slow $ease infinite;
    background: $text-muted;
    border-radius: 50%;
    height: 0.4rem;
    width: 0.4rem;

    &:nth-child(2) {
      animation-delay: $dur;
    }

    &:nth-child(3) {
      animation-delay: $dur-slow * 0.75;
    }
  }
}

/* Restoring a thread. The dots are the typing indicator's own, so the blink is
   one animation rather than a second one invented for this wait. What is new is
   the presentation: a primary disc in the middle of the log, with the sentence
   under it, because this wait is not a reply. */
.ai-chat-history {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.ai-chat-history-mark {
  align-items: center;
  background: $tint-primary;
  border-radius: $r-pill;
  display: flex;
  height: 3.4rem;
  justify-content: center;
  width: 3.4rem;

  .ai-chat-dots span {
    background: $primary;
  }
}

.ai-chat-history-label {
  color: $text-muted;
  font-family: $headings-font-family;
  font-size: 0.82rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
}

/* The chip row, and its fold.

   The site's collapse, from `_mixins.scss`, which is also what the header's
   submenu and the reasoning trace below use. The mechanic and the reasons for it
   live there; what is this file's is the inset.

   The row's padding is zero while folded and returns with the open state. It
   sits outside the grid track, so leaving it would hold a folded row open by
   its own inset and leave a gap between the log and the form. The hairline is
   the same: a closed row has no rule, or the form's own rule and this one would
   stack. Both are passed to the mixin as extra transition properties rather
   than restated here, so the two the mechanic owns, the track and `visibility`,
   cannot fall out of the list. */
.ai-chat-chips {
  @include collapse($also: (padding $dur $ease, border-top-width $dur $ease));

  border-top: 0 solid $line;
  padding: 0;

  &.is-open {
    border-top-width: 1px;
    padding: 0.55rem 1.25rem 0.65rem;
  }
}

.ai-chat-chips-row {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

/* The labels are model output, so they are not the short strings the fixed
   labels used to be. Two lines is the cap: a third would push the input off a
   phone, and the chip is a question rather than an essay. `overflow-wrap` is
   what stops a long unbroken word from widening the row instead of wrapping.
   A suggestion and a choice share this option. A suggestion keeps the tinted
   fill and the primary type. A choice is the plain option, and the letter
   disc is what it adds. */
.ai-chat-chip {
  align-items: center;
  background: $tint-primary-soft;
  border: 1px solid $line;
  border-radius: $r-md;
  color: $primary;
  display: flex;
  font-family: $headings-font-family;
  font-size: 0.82rem;
  font-weight: 600;
  gap: 0.6rem;
  overflow: hidden;
  padding: 0.45rem 0.7rem;
  text-align: left;
  width: 100%;
  transition: background-color $dur-quick $ease, border-color $dur-quick $ease, color $dur-quick $ease;

  &:hover,
  &:focus-visible {
    background: $tint-primary;
    border-color: $primary;
    color: $primary;
  }

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 2px;
  }
}

.ai-chat-chips.is-choices .ai-chat-chip {
  background: $ground;
  color: $text-strong;

  &:hover,
  &:focus-visible {
    background: $tint-primary-soft;
    border-color: $primary;
    color: $text-strong;
  }
}

/* The chips arrive one after another, the way the site's repeated blocks do.

   The animation is hung on the open state rather than on the chip, because the
   row is kept rendered: a node that never remounts never replays a mount
   animation, so the class that changes has to be the thing that declares it. The
   delay comes from the chip's own position, which is set as a custom property on
   the element, so a longer set staggers as far as it needs to and there is no
   second number to keep in step with the count. */
.ai-chat-chips.is-open .ai-chat-chip {
  animation: ai-chat-rise $dur $ease both;
  animation-delay: calc(var(--chip-index, 0) * 45ms);
}

.ai-chat-chip-mark {
  align-items: center;
  background: $primary;
  border-radius: 50%;
  color: $ground;
  display: inline-flex;
  flex: none;
  font-size: 0.72rem;
  font-weight: 700;
  height: 1.35rem;
  justify-content: center;
  line-height: 1;
  width: 1.35rem;
}

.ai-chat-chip-label {
  display: -webkit-box;
  flex: 1 1 auto;
  min-width: 0;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: normal;
}

/* The log's box, and the anchor the jump control floats against. The log
   itself scrolls, so the control cannot live inside it or it would scroll away
   with the transcript. */
.ai-chat-log-wrap {
  position: relative;
}

/* The way back to the newest text, over the bottom of the transcript.

   Out of the flow, so opening it does not push the chips or the field. Closed
   it is invisible and ignores the pointer: `visibility` is what takes it out of
   the tab order, and `pointer-events` is what keeps a fading pill from eating
   a click meant for the message underneath. The ground is solid because the
   outline button is otherwise transparent, and a transparent pill over a
   message cannot be read. */
.ai-chat-jump.app-btn {
  background: $ground;
  border-radius: $r-pill;
  bottom: 0.85rem;
  box-shadow: $shadow-card;
  left: 50%;
  opacity: 0;
  padding: 0.45rem 0.9rem;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%) translateY(0.35rem);
  transition: opacity $dur $ease, transform $dur $ease, visibility $dur $ease,
    background-color $dur-quick $ease, border-color $dur-quick $ease, box-shadow $dur $ease,
    color $dur-quick $ease;
  visibility: hidden;
  white-space: nowrap;
  z-index: 1;

  &.is-open {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(-50%);
    visibility: visible;
  }

  /* The outline hover fills with a 14% tint. Over a message that tint reads as
     the pill turning transparent, so the hover keeps the same white ground. */
  &:not(.app-btn--disabled):not([disabled]):hover,
  &:not(.app-btn--disabled):not([disabled]):focus-visible {
    background: $ground;
  }
}

.ai-chat-form {
  border-top: 1px solid $line;
  display: flex;
  flex-direction: column;
  padding: 0.9rem 1.25rem;
}

.ai-chat-checks {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.ai-chat-check-wait {
  align-items: center;
  background: $ground;
  border: 1px solid $line;
  border-radius: 0.15rem;
  color: $primary;
  display: flex;
  font-size: 0.9rem;
  font-weight: 500;
  gap: 0.6rem;
  height: 65px;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.ai-chat-compose {
  display: flex;
  gap: 0.6rem;
}

.ai-chat-input {
  background: $ground;
  border: 0;
  color: $text-heading;
  flex: 1 1 auto;
  font-size: 0.95rem;
  min-width: 0;
  padding: 0.55rem 0;

  /* _reset.scss strips every focus ring with !important, but this field is
     distinguished by the card it sits in and the caret, so it has no border to
     colour and the ring would only outline an invisible box. */
  &:focus {
    outline: none;
  }

  &::placeholder {
    color: $text-muted;
  }

  &:disabled {
    color: $text-muted;
  }
}

/* The send control is AppButton, so the site still has one button. Only its
   padding is local: an icon-only control in a borderless input row needs to be
   square rather than a wide pill. */
.ai-chat-send {
  padding: 0.7rem 1rem;
}

/* The state line. It sits under the form rather than in the log, because it is
   a state of the surface and not something the assistant said. */
.ai-chat-notice {
  border-top: 1px solid $line;
  color: $text-label;
  font-size: 0.82rem;
  line-height: 1.5;
  margin: 0;
  padding: 0.75rem 1.25rem;
}

/* Visually hidden, still announced. Used for the input's label, the typing
   indicator's text and the live region. */
.ai-chat-sr {
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

/* One entrance for every bubble, at the site's own duration. */
@keyframes ai-chat-rise {
  from {
    opacity: 0;
    transform: translateY(0.4rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes ai-chat-blink {
  0%,
  60%,
  100% {
    opacity: 0.35;
  }

  30% {
    opacity: 1;
  }
}

/* iOS Safari zooms the whole page when a focused field is under 16px, and the
   input is 0.95rem. Only the phone step is raised; the desktop field keeps its
   size. */
@media (max-width: 767.98px) {
  .ai-chat-input {
    font-size: 1rem;
  }
}

/* The band sizes against `dvh` on a phone. `vh` is the height the viewport
   would have if the on-screen keyboard were not there, so a `vh` cap lets the
   keyboard push the input off the screen and the visitor types blind. `vh` is
   kept as the line before it as the fallback for browsers without `dvh`.

   Only the band reaches this. The panel's own `.ai-chat.is-panel .ai-chat-log`
   outranks a bare class, so it keeps the 58dvh pair it declared above.

   The floor is bounded here for the same reason it is in the panel: a floor
   above the cap wins, so an unguarded 22rem would be a 352px box on a phone
   shorter than about 570px and the cap would not be a cap. */
@media (max-width: 575.98px) {
  .ai-chat-log {
    min-height: min(22rem, 62vh);
    min-height: min(22rem, 62dvh);
    max-height: 62vh;
    max-height: 62dvh;
  }
}
</style>

<script>
import { faArrowDown, faPaperPlane, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import StatusDot from '@/Components/StatusDot.vue';
import { renderMarkdown } from '@/markdown';
import { scrollDuration, scrollEase } from '@/smoothScroll';
import { hasFinePointer } from '@/pointer';
import { claimChat, releaseChat } from '@/turnstile';

FontAwesomeLibrary.add(faArrowDown, faPaperPlane, faSpinner);

/* A counter rather than a fixed id, because the id is paired with a `<label for>`
   and two mounted instances would otherwise share one id and one label pointing
   at the wrong field. The AI route only ever shows one panel today, but the
   floating launcher and the in-page box are both this component, so "only one
   is mounted" is a fact about the routes rather than about the component.
   Module scope, so the number is per document rather than per component. */
let instances = 0;

/* How close to the bottom counts as following the answer. Reading scrollback is
   the one thing a chat panel must not fight, so once the visitor scrolls up
   past this the arriving tokens stop pulling them back down. */
const FOLLOW_THRESHOLD = 48;

/* How long a disclosure's collapse runs, mirroring `$dur` in _tokens.scss.

   Spelled here because a disclosure that opens over time has to be held still
   over the same time, and the scroll correction below needs to know when the
   motion it is compensating for has finished. The alternative, reading the
   element's computed `transition-duration` back out, is a style read for a
   number the site already fixes at one value. */
const COLLAPSE_MS = 250;

export default {
  components: {
    AppButton,
    StatusDot
  },

  props: {
    /* One turn per entry: `{ id, role, text, reasoning }`, where the role is
       `bot` or `user`. `reasoning` is `{ text, ms }` on a bot turn the model
       narrated, and null on every other. The transcript is the composable's,
       not this component's. */
    messages: {
      type: Array,
      default: () => []
    },

    /* The newest set of follow-up questions, or an empty array. Rendered as
       plain button text and never as HTML. */
    suggestions: {
      type: Array,
      default: () => []
    },

    /* The chips on screen belong to a turn older than the newest one.

       What it does is hold the chip row folded until the set for the newest
       turn has arrived, so the row the visitor just used does not sit there
       offering the questions they have already moved past. */
    suggestionsStale: {
      type: Boolean,
      default: false
    },

    /* `choices` when the chips are the answers to the question in the newest
       reply. `suggestions` when they are the usual follow-up questions. */
    chipMode: {
      type: String,
      default: 'suggestions'
    },

    /* An answer is in flight. True from the moment a message is sent until the
       reply has been persisted, which is what stops the typing indicator the
       moment the answer is real rather than when the stream closes. */
    isTyping: {
      type: Boolean,
      default: false
    },

    /* The check is in progress and the turn has not started. The send button
       spins on this; the reply bubble stays down until the token is in hand. */
    checking: {
      type: Boolean,
      default: false
    },

    /* The checkbox is on screen, so the wait line is no longer the thing to
       look at. */
    challengeOpen: {
      type: Boolean,
      default: false
    },

    /* Cards for the turn in flight, drawn before the message. */
    liveCards: {
      type: Array,
      default: () => []
    },

    /* The part of the answer that has arrived so far. Empty between turns. */
    streamingText: {
      type: String,
      default: ''
    },

    /* The model's narration for the turn in flight, as it is written. This is
       the summary the provider returns on its own stream, ahead of the answer,
       and never a part of it. It is not persisted anywhere, so this is the only
       copy: once the answer lands it moves onto the finished message in memory
       and a reload shows the answer without it. */
    reasoningText: {
      type: String,
      default: ''
    },

    /* That narration is still being written. What it drives is the label: the
       moment it goes false the disclosure reads as a finished figure rather
       than as work in progress. */
    reasoningActive: {
      type: Boolean,
      default: false
    },

    /* How long the narration took, in milliseconds, from the send to the first
       token of the answer. */
    reasoningMs: {
      type: Number,
      default: 0
    },

    /* Which finished reasoning traces are open, keyed by message index. Owned by
       the composable, because the disclosure is part of the conversation: the
       two surfaces over this thread have to show the same traces open. */
    openThinks: {
      type: Object,
      default: () => ({})
    },

    /* Whether the live trace is open. The model's own fold writes this too, so
       a fold by the visitor and the fold on the answer's first token are one
       state rather than two. */
    thinkOpen: {
      type: Boolean,
      default: true
    },

    /* The message being typed, owned by the composable so that one thread has
       one composer. Written through `update:draft` on every keystroke. */
    draft: {
      type: String,
      default: ''
    },

    /* Whether this surface is the one that reads finished turns out loud.

       A live region announces whenever it changes, so two surfaces over one
       thread would each announce every answer and a screen reader would read
       the whole transcript twice. The chrome that shares its page with another
       surface turns this off and leaves the announcing to it. */
    announce: {
      type: Boolean,
      default: true
    },

    /* The assistant cannot answer: no key, a dead gateway, a rotated token.
       The surface disables the field and says so rather than leaving the
       visitor waiting on a bubble that never arrives. */
    disabled: {
      type: Boolean,
      default: false
    },

    /* The last thing that went wrong, as a key rather than as a sentence. The
       composable knows which state it is in; this file owns the surface's
       words, so it is this file that says them. Empty when nothing has. */
    notice: {
      type: String,
      default: ''
    },

    /* Whether the transcript has been asked for and answered.

       Defaults to true so a surface wired to nothing still shows its greeting
       rather than a loading mark that never resolves. It is only ever false
       while the history request is in flight. */
    hydrated: {
      type: Boolean,
      default: true
    },

    /* Which chrome the panel is drawn in.

       The conversation is the same in both places; the card around it is not.
       In the page band the box is the band's focus, so it is capped at 46rem
       and carries the band's own shadow, which travels far enough below the card
       that it needs the 4rem margin under it. In the floating panel it is a
       window over the page, sized by the launcher, and a band's shadow on a
       26rem card is a smudge rather than depth.

       A prop rather than a second panel, and rather than the launcher reaching
       into this component with `:deep`: the card belongs to this file, so the
       two ways of drawing it are declared here, next to each other. */
    variant: {
      type: String,
      default: 'band',
      validator: (value) => ['band', 'panel'].indexOf(value) !== -1
    }
  },

  emits: ['send', 'update:draft', 'toggle-think', 'set-think-open'],

  data() {
    return {
      inputId: `ai-chat-input-${instances += 1}`,

      /* The newest finished turn, read out once. See the `messages` watcher. */
      announcement: '',

      /* Whether arriving tokens may pull the log back to the bottom. */
      following: true,

      /* The visitor has taken the log, by wheel, touch or a press. Until then a
         scroll is the pictures arriving, and it must not be read as them
         leaving the bottom. */
      taken: false,

      /* Whether a glide to the bottom is in flight, so a scroll it caused itself
         is not mistaken for the visitor scrolling away. See `onScroll`. */
      gliding: false,

      /* A disclosure is open or closing and its height is being held against the
         reading position. See `holdInPlace`. */
      holdFrame: 0
    };
  },

  computed: {
    /* The same shared publisher the chat title bar and the page head use, so a
       rename reaches this header too. */
    name() {
      const business = this.$page.props.business || {};
      return business.name || this.$page.props['app.name'];
    },

    /* No turns yet and nothing on the way, so the panel is showing its empty
       state. The greeting is not a message, which is why this is a plain count
       rather than a comparison against a seeded length.

       Gated on `hydrated` because an unanswered question about the transcript is
       not the same answer as "there is nothing here". Without the gate the
       greeting was drawn for the length of the history request and then replaced
       by the turns it was supposed to be standing in for. */
    isEmpty() {
      return this.hydrated && this.messages.length === 0 && !this.isTyping;
    },

    /* Waiting to be told whether this thread is empty. The log holds its shape
       while it waits, because the floor in `.ai-chat-log` is what gives the empty
       state the size of the window it is about to fill. */
    isHydrating() {
      return !this.hydrated && this.messages.length === 0 && !this.isTyping;
    },

    /* How many cards are on screen. A live pack grows by one, and that is the
       signal to follow the log. A restored transcript arrives with its cards
       already in place, so the count jumps by more than one and is left to the
       history jump. */
    cardCount() {
      const finished = this.messages.reduce((count, message) => count + (message.cards ? message.cards.length : 0), 0);

      return finished + this.liveCards.length;
    },

    /* Whether the way back to the newest text is on offer.

       The condition is the follow flag itself. The log follows while its own
       bottom is within a threshold of the window, so not following means there
       is text below the fold, which is exactly when a way down is worth
       offering. Written as that one flag rather than as a second measurement of
       the same distance, so this cannot disagree with the rule that decides
       whether arriving tokens may pull the visitor down.

       It is not tied to a turn in flight, which is how it started. A finished
       conversation usually has nothing new to show, and that reasoning holds
       until the one case it matters: an answer that lands while the visitor is
       reading scrollback. Tying it to the turn hid the control at the exact
       moment they were furthest from the bottom and had the most to travel.
       Sending re-arms the follow, so the two never argue about which is
       showing. */
    showJump() {
      return !this.following;
    },

    /* Which of the two chromes this instance is drawn in. */
    isPanel() {
      return this.variant === 'panel';
    },

    /* Whether the chip row is folded away.

       Two reasons, one state. A turn in flight and a superseded row are the
       same moment from the visitor's side: the answer is being worked out and
       the questions that follow it do not exist yet. The second is an empty
       set, which folds the row to nothing rather than leaving its bottom
       padding as a gap between the log and the form. */
    chipsFolded() {
      return this.isTyping || this.suggestionsStale || this.suggestions.length === 0;
    },

    /* The field's value, two-way bound onto the shared draft. A computed rather
       than `data()`, because the string belongs to the conversation and not to
       this surface: both surfaces show the same one, and sending from either
       clears both. The get reads the prop, the set emits, and the composable is
       the only thing that actually holds it. */
    draftText: {
      get() {
        return this.draft;
      },
      set(value) {
        this.$emit('update:draft', value);
      }
    },

    /* The one line under the form, and which state gets it.

       An assistant that cannot answer at all outranks a single failed attempt,
       because "try again" is the wrong advice when there is nothing to try
       again on. Otherwise the notice key decides, and a key this file does not
       know renders nothing rather than a raw key. */
    stateLine() {
      if (this.disabled) {
        return this.t('The assistant is offline right now. Leave us a message at the bottom of the page and the team will reply.');
      }

      if (this.notice === 'throttled') {
        return this.t('Too many messages at once. Try again in a moment.');
      }

      if (this.notice === 'turnstile') {
        return this.t('The anti-bot check failed. Please try again.');
      }

      if (this.notice === 'failed') {
        return this.t('The assistant could not answer just now. Try again in a moment.');
      }

      return '';
    },

    /* The reply stays off the log while the narration is open and nothing of
       the answer has been revealed yet. The answer is already in hand; it waits
       so the trace can finish. Before any narration, and again once the trace
       has folded, the bubble is the dots or the reply being written. */
    showLiveAnswer() {
      if (this.liveCards.length && !this.streamingText) {
        return false;
      }

      return !(this.reasoningText && this.thinkOpen && !this.streamingText);
    },

    /* The live bubble's HTML, drawn from the revealed text itself.

       It used to be a local string a watcher filled one frame later. That
       misses the text that is already there when a second surface mounts: the
       watcher does not run for the value the prop arrived with, so the new
       panel drew the dots while the one that had been open the whole time drew
       the answer. The reveal already advances this string once a frame, so
       there is no second buffer to keep. */
    streamHtml() {
      const text = this.streamingText;

      if (!text) {
        return '';
      }

      return renderMarkdown(text);
    },

    /* The live trace, same reason as `streamHtml`. A finished turn is memoised
       in `bodyHtml`; this one changes every frame, and memoising each prefix
       would keep every intermediate render for the life of the surface. */
    reasoningHtml() {
      const text = this.reasoningText;

      if (!text) {
        return '';
      }

      return renderMarkdown(text);
    },

    /* The server's `assistant.input_chars`, shared through Inertia so the
       field's maxlength and the validator's rule are one number. The fallback
       is that same config default, for a surface that renders before the prop
       has arrived. */
    maxLength() {
      const assistant = this.$page.props.assistant || {};
      const chars = Number(assistant.input_chars);

      return chars > 0 ? chars : 1500;
    }
  },

  watch: {
    /* The live bubble grows the log as the reveal advances, so it follows the
       bottom the same way a finished turn does. The HTML itself is not written
       here: it is computed from this same prop, which is what keeps a surface
       that mounts mid-answer from drawing the dots over text the other surface
       is already showing. */
    streamingText() {
      this.scrollToLatest();
    },

    /* The narration grows the log as it arrives, so it follows the bottom the
       same way the answer does. */
    reasoningText() {
      this.scrollToLatest();
    },

    /* A finished turn is read out once, and a restored transcript is not read out
       at all.

       The count moving by exactly one is a turn the visitor just watched being
       answered, so it is announced. Anything else is the transcript arriving
       whole from history, which is the one jump the count cannot make by being
       added to one turn at a time. The two want opposite treatment: a live turn
       is announced and followed only if the visitor is at the bottom, while a
       restored conversation is silent and always lands at the newest turn,
       because the visitor has no reading position in a transcript that was not
       on screen a moment ago. */
    'messages.length'(next, previous) {
      if (next !== previous + 1) {
        this.jumpToLatest();

        /* The pictures in a restored thread have no height yet. The pin above
           lands on the text, and the cards then open underneath it. A second
           pin after the paint, and another as each picture arrives, is what
           puts the newest turn at the bottom once the cards have their size. */
        this.$nextTick(() => {
          if (!this.taken) {
            this.jumpToLatest();
          }
        });

        return;
      }

      const newest = this.messages[this.messages.length - 1];

      if (newest) {
        this.announcement = String(newest.text || '');
      }

      /* A reply that arrives while the visitor is reading scrollback must not
         pull them away from it. Sending is what re-arms following, not
         receiving.

         This one glides, and the two watchers above do not. A turn is one block
         of text arriving at once, so there is a distance to travel and a glide
         is what makes it read as the log moving rather than jumping. The
         answer's own tokens are the log growing a few pixels at a time, and a
         tween chasing a target that moves every frame never arrives at it: at
         that scale the pin is both correct and smoother. */
      this.scrollToLatest(true);
    },

    /* One card of a pack just arrived. Follow it the same way a finished turn
       is followed, and only when it is the single next card rather than a
       transcript being restored. */
    cardCount(next, previous) {
      if (next === previous + 1) {
        this.scrollToLatest(true);
      }
    }
  },

  created() {
    /* Replies are re-rendered on every frame of a streaming answer, and the
       parse is not free, so a finished reply keeps the HTML it already
       produced. Not declared in `data()` on purpose: in `data()` it would be a
       reactive collection, and writing to a reactive collection from inside the
       render that reads it is a loop. Keyed by the text itself, which is what
       changes when a reply changes. */
    this.rendered = Object.create(null);
  },

  mounted() {
    /* A surface can mount onto a conversation that is already in memory: the AI
       page's band restores one when it mounts, and the launcher is reopened onto
       one on a later page. The log renders from its top, which is the oldest
       turn, so it starts where the visitor did not leave it. */
    if (this.messages.length) {
      this.jumpToLatest();
    }

    const sitekey = this.$page.props.turnstile && this.$page.props.turnstile.sitekey;

    if (sitekey) {
      claimChat(this.$refs.messageCheck, sitekey);
    }
  },

  beforeUnmount() {
    /* A glide holds a frame of its own, and it is cancelled by `stopGlide`
       rather than inline so the handle and the flag cannot be left disagreeing
       about whether one is running. */
    this.stopGlide();

    /* Same for a disclosure hold, whose frame is correcting a scroll and must
       not outlive the log it is correcting. */
    this.stopHold();
    releaseChat(this.$refs.messageCheck);
  },

  methods: {
    submit() {
      const value = this.draftText.trim();

      if (!value || this.isTyping || this.checking || this.disabled) {
        return;
      }

      /* The field stays until the check has passed. `send` clears it, and it
         clears both surfaces together, once the turn is actually starting. */
      this.ask(value);
    },

    /* The one way a question leaves this panel, whichever control asked it.

       The chip and the form are the same act, so they are one method. A chip
       used to emit straight to the parent, which meant the re-arm below reached
       a typed question and not a clicked one: a visitor reading scrollback could
       click a chip and the log would stay exactly where it was while the
       question they had just asked streamed in below the fold. */
    choiceLetter(index) {
      return 'ABC'.charAt(index);
    },

    ask(text) {
      /* Sending re-arms the follow, whatever the visitor was reading. */
      this.following = true;
      this.taken = false;
      this.$emit('send', text);
    },

    /* The public way into this panel's field.

       The floating launcher opens the panel, so it is the launcher that has to
       move focus into it; reaching into `$refs.field` from there would make the
       field's DOM a contract between two components. With a disabled assistant
       the field cannot take focus, so the log does, which is the scrollable
       region that holds the reason.

       On a touch device the log takes it instead of the field, even when the
       assistant can answer. Focusing a text input there raises the on-screen
       keyboard, and the panel opens onto a conversation: on a phone the visitor
       would meet a keyboard covering the half of the panel that holds the
       greeting, the restored turns and the chips, before they had read any of
       it. Tapping the field is still how the keyboard is asked for, which is the
       same gesture every other field on the site uses.

       `hasFinePointer` is the same capability check the cursor and the services
       spotlight use, so there is one answer to "is there a mouse" on the site
       rather than three spellings of the media query. */
    focus() {
      this.$nextTick(() => {
        const target = this.disabled || !hasFinePointer() ? this.$refs.log : this.$refs.field;

        if (target) {
          target.focus();
        }
      });
    },

    /* Memoised so a finished reply is parsed once rather than once per frame of
       the next one. */
    bodyHtml(text) {
      const key = String(text == null ? '' : text);

      if (!(key in this.rendered)) {
        this.rendered[key] = renderMarkdown(text);
      }

      return this.rendered[key];
    },

    /* The one wording for the disclosure, used by the live trace and by every
       finished one. The two differ only in where the figure comes from, so the
       sentence is written once: `active` is the live case, and a finished one
       reads the figure it was handed. */
    thinkLabel(ms, active) {
      if (active) {
        return this.t('Thinking');
      }

      /* Rounded up to a whole second and floored at one, because "Thought for
         0s" is not a thing that happened. */
      return this.t('Thought for {seconds}s', {
        seconds: Math.max(1, Math.round((Number(ms) || 0) / 1000))
      });
    },

    /* Per finished turn. The row is collapsed unless the composable says it is
       open, and the click is emitted rather than written here: the state is the
       conversation's, so it is the composable that changes it. */
    isThinkOpen(index) {
      return this.openThinks[index] === true;
    },

    toggleThink(index, event) {
      this.holdInPlace(event.currentTarget, () => this.$emit('toggle-think', index));
    },

    /* The live trace, opened and closed through the same state the model's own
       fold writes. */
    setThinkOpen(value, event) {
      this.holdInPlace(event.currentTarget, () => this.$emit('set-think-open', value));
    },

    /* Toggles a disclosure without the log sliding out from under it.

       Opening a trace grows the log by the height of the narration, and that
       growth lands above whatever the visitor is reading: with the scroll left
       alone, everything below the trace walks down by the same amount and the
       line being read is lost. The browser's own scroll anchoring cannot be
       counted on for this, because the log is a flex column and the growth is
       inside it.

       So the head that was clicked is measured against the log before the
       toggle and put back at the same offset after it. The head is the right
       thing to hold still rather than the bottom of the log: it is the row the
       visitor aimed at, and the narration opens underneath it.

       The reading happens in the microtask after the DOM updates and before the
       browser paints, so the correction is applied in the same frame as the
       growth rather than as a visible jump a frame later. Measuring after the
       update rather than predicting the growth is what makes this work whether
       or not the browser anchored anything itself: a residual of zero simply
       means it already did.

       It runs for the length of the collapse rather than once. The trace's block
       grows over `$dur` as the track animates, so a single correction would
       account for the first frame's share of it and leave the reading position
       to slide down by all the rest. Re-measuring per frame is also what keeps
       the correction honest if anything else moves the log in the meantime.

       `following` is re-read the same way a scroll would have read it, because
       the toggle moved the log without a scroll of the visitor's making, and the
       flag is a property of where the log is rather than of what was clicked. If
       the disclosure grew downwards out of view, the log is genuinely no longer
       at the bottom, and the arriving answer must stop pulling at it. */
    holdInPlace(head, change) {
      const log = this.$refs.log;

      if (!log || !head) {
        change();

        return;
      }

      const offset = head.getBoundingClientRect().top - log.getBoundingClientRect().top;

      /* A glide still running would be writing `scrollTop` against the
         correction below, so it is ended before the disclosure moves anything. */
      this.stopGlide();
      this.stopHold();

      change();

      const until = window.performance.now() + COLLAPSE_MS;

      const step = (now) => {
        const moved = head.getBoundingClientRect().top - log.getBoundingClientRect().top;

        log.scrollTop += moved - offset;

        if (now >= until) {
          this.holdFrame = 0;
          this.readFollowing();

          return;
        }

        this.holdFrame = window.requestAnimationFrame(step);
      };

      this.holdFrame = window.requestAnimationFrame(step);
    },

    stopHold() {
      if (this.holdFrame) {
        window.cancelAnimationFrame(this.holdFrame);
        this.holdFrame = 0;
      }
    },

    /* Following is a property of the scroll position rather than of the
       message, so it is read off the element instead of being assumed. */
    onScroll() {
      /* A glide's own scroll events are not the visitor scrolling. The log is on
         its way to the bottom and following is true by construction, so reading
         the flag off a position halfway there would turn the follow off
         mid-glide, and the answer arriving a moment later would be left where it
         was. A wheel or a drag calls `releaseFollow` first, which ends the glide,
         so a scroll that arrives after that one is the visitor's.

         A picture loading grows the log without a scroll of theirs. Until they
         have taken it, that growth is not them leaving the bottom. */
      if (this.gliding || !this.taken) {
        return;
      }

      this.readFollowing();
    },

    /* A card picture finished loading. If the visitor is still with the newest
       turn, the picture's height has just opened under the pin, so the pin is
       put back. */
    onCardImage() {
      if (this.taken && !this.following) {
        return;
      }

      this.following = true;
      this.scrollToLatest();
    },

    /* The visitor took the log. A glide in progress is writing `scrollTop` on
       its own frames, and those frames would otherwise keep winning: the scroll
       they cause is ignored above, so following would stay on and the way back
       would never appear. Ending the glide here lets the next scroll be read
       as theirs. */
    releaseFollow() {
      this.taken = true;

      if (this.gliding) {
        this.stopGlide();
      }
    },

    /* The one place the rule is written, so a scroll and a disclosure toggle
       cannot end up reading it two ways. */
    readFollowing() {
      const log = this.$refs.log;

      if (!log) {
        return;
      }

      this.following = (log.scrollHeight - log.scrollTop - log.clientHeight) <= FOLLOW_THRESHOLD;
    },

    /* Moves the log to the newest turn and re-arms following.

       Separate from `scrollToLatest`, which defers to the visitor: this one is
       for a log that has just been filled or refilled whole, where the reading
       position is either meaningless or was never the visitor's to keep. It is
       also what a restored conversation needs, and it is the only reason
       following is re-armed anywhere except a send.

       `smooth` is the jump control. A restored transcript still pins, because
       there is no distance the visitor was watching; the control is a press
       they made while looking at the scrollback, so the log travels. */
    jumpToLatest(smooth) {
      this.following = true;
      this.taken = false;
      this.scrollToLatest(smooth);
    },

    /* The log's own `scrollTop`, deliberately. The document's scrolling is a
       requestAnimationFrame tween owned by smoothScroll.js, so `scrollIntoView`
       would also scroll the page behind the panel.

       `smooth` is for a distance the visitor is watching: a turn that has just
       arrived whole, and the jump control. Everything else is a pin, because a
       streamed token is the log growing by a few pixels and the answer has to
       be at the bottom the moment it is written. */
    scrollToLatest(smooth) {
      /* A hold is moving the log to keep a disclosure's head still, and a pin
         would be writing the same property towards the other end of the log. */
      if (!this.following || this.holdFrame) {
        return;
      }

      this.$nextTick(() => {
        /* Read again, inside the tick. The guard above was true when this pin
           was queued, which is one token ago: the visitor can have scrolled away
           in between, and a pin that still writes puts them back at the bottom.
           That write fires `scroll`, which reads the position it just forced and
           turns following back on, so the way back never gets a frame to show.
           A pin that finds the visitor has left simply does not write. */
        if (!this.following || this.holdFrame) {
          return;
        }

        const log = this.$refs.log;

        if (!log) {
          return;
        }

        /* A pin yields to a glide that is already running. The jump control and
           a turn that arrived whole both travel by glide, and a token that
           landed during that travel used to cancel it and snap the rest of the
           way. The glide's last frame writes the live bottom, so yielding loses
           nothing: the log still ends on the newest text, it just gets there
           in one motion. A new glide replaces the one in flight, because its
           target is the bottom as of now rather than as of the glide it
           interrupts. */
        if (this.gliding && !smooth) {
          return;
        }

        this.stopGlide();

        /* Floored at zero because the log has a minimum height: a conversation
           shorter than it cannot scroll at all, and its "bottom" is its top. */
        const target = Math.max(0, log.scrollHeight - log.clientHeight);

        if (!smooth || Math.abs(target - log.scrollTop) < 1) {
          log.scrollTop = log.scrollHeight;

          return;
        }

        this.glide(log, target);
      });
    },

    /* Carries the log to the bottom over the site's own duration and curve,
       taken from smoothScroll.js so that a chat follow moves the way a page
       scroll does.

       The target is fixed when the glide starts rather than re-read each frame.
       That is what keeps it a glide: a tween chasing a target that moves on
       every token would spend the whole answer arriving nowhere. Tokens that
       arrive during the travel wait, and the last frame writes the live bottom
       rather than the target measured at the start, so a log that grew on the
       way still lands on it. */
    glide(log, target) {
      const start = log.scrollTop;
      const distance = target - start;
      const duration = scrollDuration(distance);
      const startedAt = window.performance.now();

      this.gliding = true;

      const step = (now) => {
        const t = Math.min(1, (now - startedAt) / duration);

        log.scrollTop = start + distance * scrollEase(t);

        if (t < 1) {
          this.glideFrame = window.requestAnimationFrame(step);

          return;
        }

        /* The last write is the log's own bottom rather than the target measured
           a moment ago, so a glide that ends while the log is still growing
           still lands on it. */
        this.glideFrame = 0;
        this.gliding = false;
        log.scrollTop = log.scrollHeight;
      };

      this.glideFrame = window.requestAnimationFrame(step);
    },

    stopGlide() {
      if (this.glideFrame) {
        window.cancelAnimationFrame(this.glideFrame);
        this.glideFrame = 0;
      }

      this.gliding = false;
    },
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Always online 24/7': 'Always online 24/7',
          'Typing': 'Typing',
          'Loading the conversation': 'Loading the conversation',
          'Jump to latest': 'Jump to latest',
          'Thinking': 'Thinking',
          'Thought for {seconds}s': 'Thought for {seconds}s',
          'Your question': 'Your question',
          'Type your question...': 'Type your question...',
          'Or type your own answer': 'Or type your own answer',
          'Send': 'Send',
          'The assistant is offline right now. Leave us a message at the bottom of the page and the team will reply.': 'The assistant is offline right now. Leave us a message at the bottom of the page and the team will reply.',
          'Too many messages at once. Try again in a moment.': 'Too many messages at once. Try again in a moment.',
          'The assistant could not answer just now. Try again in a moment.': 'The assistant could not answer just now. Try again in a moment.'
        },
        'zh-MY': {
          'Always online 24/7': '24/7 全天在线',
          'Typing': '正在输入',
          'Loading the conversation': '正在载入对话',
          'Jump to latest': '跳到最新',
          'Thinking': '正在思考',
          'Thought for {seconds}s': '思考了 {seconds} 秒',
          'Your question': '您的问题',
          'Type your question...': '输入您的问题...',
          'Or type your own answer': '或输入您自己的答案',
          'Send': '发送',
          'The assistant is offline right now. Leave us a message at the bottom of the page and the team will reply.': '助理目前无法连线。请在页面底部留言，团队会回复您。',
          'Too many messages at once. Try again in a moment.': '一次发送太多讯息了。请稍后再试。',
          'The assistant could not answer just now. Try again in a moment.': '助理刚刚无法作答。请稍后再试。'
        },
        'ms-MY': {
          'Always online 24/7': 'Sentiasa dalam talian 24/7',
          'Typing': 'Sedang menaip',
          'Loading the conversation': 'Memuatkan perbualan',
          'Jump to latest': 'Pergi ke terkini',
          'Thinking': 'Sedang berfikir',
          'Thought for {seconds}s': 'Berfikir selama {seconds} saat',
          'Your question': 'Soalan anda',
          'Type your question...': 'Taip soalan anda...',
          'Or type your own answer': 'Atau taip jawapan anda sendiri',
          'Send': 'Hantar',
          'The assistant is offline right now. Leave us a message at the bottom of the page and the team will reply.': 'Pembantu tidak tersedia buat masa ini. Tinggalkan mesej di bahagian bawah halaman dan pasukan kami akan membalas.',
          'Too many messages at once. Try again in a moment.': 'Terlalu banyak mesej sekali gus. Cuba lagi sebentar.',
          'The assistant could not answer just now. Try again in a moment.': 'Pembantu tidak dapat menjawab sebentar tadi. Cuba lagi sebentar.'
        }
      }
    });

    return { t };
  }
};
</script>
